import React, {useCallback, useState} from 'react';
import {useLocalization} from '@epic-mw/localization';
import {IndustrySelect} from './IndustrySelect';
import {validateEmail, isEmpty} from 'epic-ue-shared';
import {EmailInput} from './EmailInput';
import {useMarketoNewsletterSubscribe} from '../shared';
import {useAccountStore, useIndustryList} from 'epic-ue-shared';
import {Button} from 'epic-ue-components';
import {Legal, NewsletterSignupWrap, FieldWrap} from './Form.styles';
import {getLocale} from '@epic-core/common';

interface Properties {
    successContent?: string;
    signUpMsg: string;
    thanksMsg: string;
    staticIndustry?: string;
    newsletterSubscribed: boolean;
    disableIndustrySelect?: boolean;
    marketoLeadParams?: {[key: string]: boolean | number | string};
    epicOptIn?: boolean;
}

export const LegalRow = (props: {epicOptIn: boolean | undefined}): JSX.Element => {
    const epicOptIn = props.epicOptIn || false;
    const getMessage = useLocalization();

    return (
        <Legal className="legal-row">
            {epicOptIn ? (
                <p>{getMessage('epic.newsletter.globaloptin')}</p>
            ) : (
                <p>{getMessage('epic.newsletter.ue.globaloptin')}</p>
            )}
        </Legal>
    );
};

export const NewsletterSignup = (props: Properties): JSX.Element => {
    const {account} = useAccountStore();
    const industryList = useIndustryList();
    const [email, setEmail] = useState(account.email || '');
    const [isEmailReadOnly, setIsEmailReadOnly] = useState(false);
    const [emailPristine, setEmailPristine] = useState(!email);
    if (account && account.email && !email && emailPristine) {
        setEmail(account.email);
    }

    const hasEmail = Boolean(account && account.isLoggedIn && account.email);
    if (hasEmail && hasEmail !== isEmailReadOnly) {
        setIsEmailReadOnly(hasEmail);
    }

    const [emailValid, setEmailValid] = useState(true);
    const [interestsPristine, setInterestsPristine] = useState(true);
    const [interestsValid, setInterestsValid] = useState(true);

    const {
        successContent,
        signUpMsg,
        thanksMsg,
        newsletterSubscribed,
        disableIndustrySelect,
        marketoLeadParams,
        epicOptIn
    } = props;

    let staticIndustry = props.staticIndustry || '';
    if (staticIndustry === 'EMPTY') staticIndustry = '';
    let staticIndustryValid = false;
    if (staticIndustry) {
        industryList.forEach((industry) => {
            if (industry.id && industry.id === staticIndustry) {
                staticIndustryValid = true;
            }
        });
        if (!staticIndustryValid) {
            staticIndustry = '';
        }
    }

    const [interests, setInterests] = useState(staticIndustry);
    if (interestsPristine && interests !== staticIndustry) {
        setInterests(staticIndustry);
    }

    const {subscribed, subscribe, loading, error} = useMarketoNewsletterSubscribe();

    const handleChange = useCallback(
        (event) => {
            if (event) {
                const target = event.target || {};
                const value = target.value.replace(/^\s+/g, '');
                const name = target.name;

                switch (name) {
                    case 'email':
                        setEmail(value);
                        setEmailPristine(false);
                        break;
                    case 'interests':
                        setInterests(value);
                        setInterestsPristine(false);
                        break;
                    default:
                        break;
                }
            }
        },
        [setEmail, setEmailPristine, setInterests, setInterestsPristine]
    );

    const validateField = useCallback(
        (event, name, value) => {
            let fieldValue;
            let fieldName;
            let isValid = true;

            if (event) {
                const target = event.target;
                fieldValue = target.value;
                fieldName = target.name;
            } else {
                fieldValue = value || '';
                fieldName = name || '';
            }

            if (fieldValue === 'placeholder') {
                fieldValue = '';
            }

            if (fieldName === 'interests' && interestsPristine && staticIndustry) {
                fieldValue = staticIndustry;
            }

            if (!emailPristine || !interestsPristine || event === null) {
                if (isEmpty(fieldValue) || (fieldName === 'email' && !validateEmail(fieldValue))) {
                    isValid = !isValid;
                }
            }

            switch (fieldName) {
                case 'email':
                    setEmailValid(isValid);
                    break;
                case 'interests':
                    setInterestsValid(isValid);
                    break;
                default:
                    break;
            }

            return isValid;
        },
        [emailPristine, interestsPristine, setEmailValid, setInterestsPristine]
    );

    const signupForNewsletter = useCallback(
        (event) => {
            event.preventDefault();
            const cleanEmail = email.replace(/\s/g, '');

            const interestsValid = validateField(null, 'interests', interests);
            const emailValid = validateField(null, 'email', cleanEmail);

            const interestsChecksOut =
                disableIndustrySelect || interestsValid || (interestsPristine && staticIndustry);

            if (emailValid && interestsChecksOut) {
                let industryName = interestsPristine && staticIndustry ? staticIndustry : interests;
                if (!industryName) {
                    industryList.forEach((industry) => {
                        if (industry.id === interests) {
                            industryName = industry.name;
                        }
                    });
                }
                subscribe(cleanEmail, industryName, marketoLeadParams);
            }
        },
        [
            email,
            interests,
            emailValid,
            interestsValid,
            emailPristine,
            interestsPristine,
            staticIndustry,
            disableIndustrySelect,
            marketoLeadParams
        ]
    );

    const getMessage = useLocalization();
    const eValid = emailValid;
    const iValid = interestsValid;
    const errorText = getMessage('epic.ue.signup.newsletter.subscribe_failed') || '';
    const emailText = getMessage('epic.enterprise.contact.us.email.text');
    const invalidEmailText = getMessage('epic.enterprise.contact.us.invalid.email.text');
    const industryText = getMessage('epic.enterprise.contact.us.industry.text');
    const requiredFieldText = getMessage('epic.enterprise.contact.us.required.field.text');
    const registrationSuccessfulText =
        successContent || getMessage('epic.ue.signup.newsletter.success_content') || '';
    const hideForm = newsletterSubscribed;

    let headlineText: string | undefined = signUpMsg;
    const buttonText = getMessage('epic.enterprise.engagement.newsletter.button.text');
    const loadingClassName = loading ? 'icon-checkmark' : '';

    let newsletterMessage = '';
    if (hideForm) {
        const backupThanksText = getMessage('epic.ue.signup.newsletter.success');
        headlineText = thanksMsg || backupThanksText;
        newsletterMessage = subscribed ? registrationSuccessfulText : '';
    } else if (error) {
        newsletterMessage = errorText;
    }

    return (
        <NewsletterSignupWrap id="newsletterSignup">
            <form className="newsletter form" onSubmit={signupForNewsletter}>
                <div className="row">{headlineText ? <h2>{headlineText}</h2> : ''}</div>
                {hideForm ? (
                    <div className="no-form">
                        <div className="message-wrapper">
                            <div className={`${error ? 'error-msg' : ''}`}>{newsletterMessage}</div>
                        </div>
                    </div>
                ) : (
                    <div className="form-wrapper">
                        <div className="row">{emailPristine}</div>
                        <div className="row email">
                            <FieldWrap className="col-xs-12">
                                <EmailInput
                                    value={email}
                                    isValid={eValid}
                                    onChange={handleChange}
                                    onBlur={validateField}
                                    name="email"
                                    validationText={invalidEmailText}
                                    labelText={emailText || ''}
                                    readonly={isEmailReadOnly}
                                />
                            </FieldWrap>
                        </div>
                        {!disableIndustrySelect ? (
                            <div className="row industries">
                                <FieldWrap className="col-xs-12">
                                    <IndustrySelect
                                        value={interests}
                                        isValid={iValid}
                                        onChange={handleChange}
                                        onBlur={validateField}
                                        labelText={industryText || ''}
                                        name="interests"
                                        options={industryList}
                                        optionValue="id"
                                        validationText={requiredFieldText || ''}
                                        optionLabel="name"
                                    />
                                </FieldWrap>
                            </div>
                        ) : (
                            ''
                        )}

                        <LegalRow epicOptIn={epicOptIn} />

                        <div className="button-row">
                            <Button
                                id="engagement-newsletter-signup"
                                title={buttonText}
                                type="secondary"
                                onClick={signupForNewsletter}
                                disabled={loading}
                                icon={{position: 'before', className: loadingClassName}}
                            />
                            <div className={`${error ? 'error-msg' : ''}`}>{newsletterMessage}</div>
                        </div>

                        {!newsletterSubscribed ? (
                            <div className="privacy-link">
                                <a
                                    href={`https://www.epicgames.com/privacypolicy?lang=${getLocale()}`}
                                    target="_blank"
                                    rel="noopener noreferrer">
                                    {getMessage('epic.footer.privacy_policy')}
                                </a>
                            </div>
                        ) : (
                            ''
                        )}
                    </div>
                )}
            </form>
        </NewsletterSignupWrap>
    );
};
